import { ExclamationTriangleIcon, ShareIcon } from '@heroicons/react/16/solid';
import { createColumnHelper, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { useAccount } from 'wagmi';
import { TRADE_PAGE_TABLE_ROWS } from '../../constants';
import { PORTFOLIO_PAGE } from '../../constants/featureFlags';
import { PORTFOLIO_TRADES_CLOSED_PNL_TAB_URL } from '../../constants/routes';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useFlattenedPages } from '../../hooks/useFlattenedPages';
import { useModalState } from '../../hooks/useModalState';
import { PositionParams, useClosedPositions } from '../../hooks/usePositions';
import { ProductType, useProductsById } from '../../hooks/useProducts';
import { usePropsAsState } from '../../hooks/usePropAsState';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { useTableSorting } from '../../hooks/useTableSorting';
import { useTailwindBreakpoint } from '../../hooks/useTailwindBreakpoint';
import {
  formatCompactDuration,
  formatDate,
  formatDollar,
  formatNumber,
  formatPercentage,
} from '../../lib/formatters';
import { localStorageKeys } from '../../lib/localstorageKeys';
import { divide, getDecimalPlaces, multiply } from '../../lib/math';
import { liquidationIconTooltip, pnlClosedRowTooltip } from '../../lib/tooltips';
import { PrettyMerge } from '../../lib/tsUtilityTypes';
import { OrderSide } from '../../types/generated';
import { Button } from '../catalyst-ui-kit/button';
import { ColoredMetric } from '../ColoredMetric/ColoredMetric';
import { DateRange } from '../DateRangeInput/DateRangeInput';
import {
  CommonTableProps,
  getCommonTableParams,
  getRowLeftBorderClassName,
} from '../Table/commonTableParams';
import { getMarketColumn } from '../Table/getMarketColumn';
import { StateTable } from '../Table/Table';
import { TwoRowCell } from '../Table/TwoRowCell';
import { Tooltip } from '../Tooltip/Tooltip';
import { PositionFillsModal } from './PositionFillsModal';

const DEFAULT_SORT = { id: 'closeAt', desc: true } as const;

const SORT_FIELD_MAP = {
  openAt: 'createdAt',
  closeAt: 'updatedAt',
  // size: 'size', // We display 'totalIncreaseQuantity', not 'size'
} as const satisfies Record<string, PositionParams['orderBy']>;

type TableData = {
  id: string;
  productId: string;
  ticker: string;
  displayTicker: string;
  side: OrderSide;
  size: bigint;
  entryPrice: bigint;
  exitPrice: bigint;
  pnl: bigint;
  pnlPercentage: bigint;
  feesAccruedUsd: bigint;
  openAt: number;
  closeAt: number;
  baseTokenName: string;
  sizeDecimals: number;
  priceDecimals: number;
  fundingUsd: bigint;
  isLiquidated: boolean;
};

type PositionsTableProps = PrettyMerge<
  {
    tableData: TableData[];
    onShowPnlCardModal: (positionId: string) => void;
    openPositionFillsModal: (positionId: string) => void;
    isConnected: boolean;
  },
  CommonTableProps
>;

export const PnlTableUi: React.FC<PositionsTableProps> = ({
  tableData,
  openPositionFillsModal,
  onShowPnlCardModal,
  // Table props
  isConnected,
  isFetching,
  isPending,
  isError,
  pagination,
  viewAllLink,
  pageSize,
  paginationKey,
  sorting,
  setSorting,
  fetchNextPage,
  setPageSize,
  pageSizeOptions,
}) => {
  const [hidePnl] = useLocalStorage(localStorageKeys.hidePnl, false);
  const isLg = useTailwindBreakpoint('lg');
  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<TableData>();

    return [
      getMarketColumn(columnHelper, {
        displaySide: !isLg,
        enableSorting: 'baseTokenName_market_side' in SORT_FIELD_MAP,
        meta: {
          accordion: {
            showInTrigger: true,
          },
        },
      }),
      columnHelper.accessor(({ side }) => side, {
        id: 'direction',
        enableSorting: 'direction' in SORT_FIELD_MAP,
        header: 'Direction',
        cell: (info) => {
          const side = info.getValue();
          const isLong = side === OrderSide.BUY;

          return (
            <span className={isLong ? 'text-contentGreen' : 'text-contentRed'}>
              {isLong ? 'Long' : 'Short'}
            </span>
          );
        },
      }),
      columnHelper.accessor(
        ({ size, sizeDecimals, baseTokenName, exitPrice }) => ({
          size,
          sizeDecimals,
          baseTokenName,
          exitPrice,
        }),
        {
          id: 'size',
          enableSorting: 'size' in SORT_FIELD_MAP,
          header: 'Size',
          meta: {
            accordion: {
              showInTrigger: true,
            },
          },
          cell: (info) => {
            const { size, sizeDecimals, baseTokenName, exitPrice } = info.getValue();
            return (
              <TwoRowCell
                primaryVal={`${formatNumber(size, {
                  minimumFractionDigits: sizeDecimals,
                  maximumFractionDigits: sizeDecimals,
                })} ${baseTokenName}`}
                secondaryVal={formatDollar(multiply(size, exitPrice))}
              />
            );
          },
        }
      ),
      columnHelper.accessor(({ entryPrice, priceDecimals }) => ({ entryPrice, priceDecimals }), {
        id: 'entryPrice',
        enableSorting: 'entryPrice' in SORT_FIELD_MAP,
        header: 'Avg. Entry Price',
        cell: (info) => {
          const { entryPrice, priceDecimals } = info.getValue();
          return formatNumber(entryPrice, {
            maximumFractionDigits: priceDecimals,
            minimumFractionDigits: priceDecimals,
          });
        },
      }),
      columnHelper.accessor(
        ({ exitPrice, priceDecimals, isLiquidated }) => ({
          exitPrice,
          priceDecimals,
          isLiquidated,
        }),
        {
          id: 'exitPrice',
          enableSorting: 'exitPrice' in SORT_FIELD_MAP,
          header: 'Avg. Exit Price',
          cell: (info) => {
            const { exitPrice, priceDecimals, isLiquidated } = info.getValue();
            const formattedExitPrice = formatNumber(exitPrice, {
              maximumFractionDigits: priceDecimals,
              minimumFractionDigits: priceDecimals,
            });
            return isLiquidated ? (
              <div className="flex items-center justify-end gap-1 text-contentRed">
                <Tooltip content={liquidationIconTooltip()}>
                  <ExclamationTriangleIcon className="inline size-3" />
                </Tooltip>
                <TwoRowCell
                  primaryVal={formattedExitPrice}
                  secondaryVal={'Liquidated'}
                  primaryClassName="text-contentRed"
                  secondaryClassName="text-contentRed"
                />
              </div>
            ) : (
              formattedExitPrice
            );
          },
        }
      ),
      columnHelper.accessor(({ id, pnl, pnlPercentage }) => ({ id, pnl, pnlPercentage }), {
        id: 'pnl_pnlPercentage',
        enableSorting: 'pnl_pnlPercentage' in SORT_FIELD_MAP,
        header: () => (
          <Tooltip dottedUnderline={false} content={pnlClosedRowTooltip()}>
            Closed P&L
          </Tooltip>
        ),
        meta: {
          accordion: {
            showInTrigger: true,
          },
        },
        cell: (info) => {
          const { id, pnl, pnlPercentage } = info.getValue();
          return (
            <TwoRowCell
              primaryVal={
                <ColoredMetric
                  value={pnl}
                  formatFn={(x) =>
                    hidePnl ? '******' : formatDollar(x, { signDisplay: 'exceptZero' })
                  }
                />
              }
              secondaryVal={
                <ColoredMetric
                  value={pnlPercentage}
                  formatFn={(x) => formatPercentage(x, { signDisplay: 'exceptZero' })}
                />
              }
              icon={
                <Button
                  text
                  color="secondary"
                  onClick={() => {
                    onShowPnlCardModal(id);
                  }}
                >
                  <ShareIcon className="!size-3.5" />
                </Button>
              }
            />
          );
        },
      }),
      columnHelper.accessor('feesAccruedUsd', {
        id: 'fees',
        enableSorting: 'fees' in SORT_FIELD_MAP,
        header: 'Fees',
        cell: (info) => {
          return formatDollar(info.getValue());
        },
      }),

      columnHelper.accessor(({ fundingUsd, side }) => ({ fundingUsd, side }), {
        id: 'funding_size',
        enableSorting: 'funding_size' in SORT_FIELD_MAP,
        header: 'Funding',
        cell: (info) => {
          const { side, fundingUsd } = info.getValue();
          return (
            <span className={side === OrderSide.BUY ? 'text-contentGreen' : 'text-contentRed'}>
              {formatDollar(fundingUsd, { signDisplay: 'exceptZero' })}
            </span>
          );
        },
      }),
      columnHelper.accessor(({ openAt, closeAt }) => ({ openAt, closeAt }), {
        id: 'duration',
        enableSorting: 'duration' in SORT_FIELD_MAP,
        header: 'Open duration',
        cell: (info) => {
          const { openAt, closeAt } = info.getValue();
          return formatCompactDuration(closeAt - openAt);
        },
      }),
      columnHelper.accessor('openAt', {
        id: 'openAt',
        enableSorting: 'openAt' in SORT_FIELD_MAP,
        header: 'Open Time',
        cell: (info) => {
          return formatDate(info.getValue());
        },
        meta: { cellContainerClassName: 'text-nowrap' },
      }),
      columnHelper.accessor('closeAt', {
        id: 'closeAt',
        enableSorting: 'closeAt' in SORT_FIELD_MAP,
        header: 'Close Time',
        cell: (info) => {
          return formatDate(info.getValue());
        },
        meta: { cellContainerClassName: 'text-nowrap' },
      }),

      columnHelper.accessor(({ id }) => id, {
        id: 'actions',
        enableSorting: 'actions' in SORT_FIELD_MAP,
        header: '',
        cell: (info) => {
          return (
            <Button size="2xs" text onClick={() => openPositionFillsModal(info.getValue())}>
              Details
            </Button>
          );
        },
        meta: {
          align: 'right',
          accordion: {
            isActionColumn: true,
          },
        },
      }),
    ];
  }, [hidePnl, isLg, onShowPnlCardModal, openPositionFillsModal]);

  const table = useReactTable({
    columns,
    data: tableData,
    meta: {
      getRowClassName: getRowLeftBorderClassName,
    },
    ...getCommonTableParams({
      pageSize,
      sorting,
      setSorting,
    }),
  });

  return (
    <StateTable
      hasActions
      table={table}
      isPending={isPending}
      isFetching={isFetching}
      isError={isError}
      isConnected={isConnected}
      showPaginationControls={pagination}
      noDataText="Your closed positions will appear here."
      viewAllLink={viewAllLink}
      fetchNextPage={fetchNextPage}
      paginationKey={paginationKey}
      pageSize={pageSize}
      setPageSize={setPageSize}
      pageSizeOptions={pageSizeOptions}
    />
  );
};

export const PnlTable: React.FC<{
  products?: ProductType[];
  pagination?: boolean;
  side?: OrderSide;
  dateRange?: DateRange;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
}> = ({
  products,
  pagination,
  side,
  dateRange,
  defaultPageSize = TRADE_PAGE_TABLE_ROWS,
  pageSizeOptions = [defaultPageSize],
}) => {
  const { isConnected } = useAccount();
  const { value: portfolioPageEnabled } = useFeatureFlag(PORTFOLIO_PAGE);
  const selectedSubaccount = useActiveSelectedSubaccount();
  const firstProduct = products?.at(0);
  const productIdFilter = firstProduct ? firstProduct.id : undefined;

  const { sorting, setSorting, orderBy, order } = useTableSorting(DEFAULT_SORT, SORT_FIELD_MAP);

  const [pageSize, setPageSize] = usePropsAsState(defaultPageSize);

  const useClosedPositionParams = useMemo(
    () =>
      ({
        subaccountId: selectedSubaccount?.id,
        productIds:
          products && products.length > 0
            ? products.map((p) => p.id)
            : productIdFilter
              ? [productIdFilter]
              : undefined,
        limit: 2 * pageSize,
        order,
        orderBy,
        side,
        createdAfter: dateRange?.from?.getTime(),
        createdBefore: dateRange?.to?.getTime(),
      }) satisfies PositionParams,
    [
      order,
      orderBy,
      pageSize,
      productIdFilter,
      selectedSubaccount?.id,
      products,
      side,
      dateRange?.from,
      dateRange?.to,
    ]
  );

  const {
    data: positionsPages,
    isPending,
    isFetching,
    isError,
    fetchNextPage,
    isFetchingNextPage,
  } = useClosedPositions(useClosedPositionParams);

  const positions = useFlattenedPages('data', positionsPages?.pages);

  const setPnlCardModal = useModalState((state) => state.setPnlCardModal);
  const setPositionFillsModal = useModalState((state) => state.setPositionFillsModal);
  const productsById = useProductsById();

  const positionWithProduct = useMemo(
    () =>
      positions?.map((position) => ({
        ...position,
        product: productsById?.[position.productId],
      })),
    [positions, productsById]
  );

  const tableData = useMemo(
    () =>
      positionWithProduct?.map((position) => {
        const product = position.product;
        return {
          id: position.id,
          productId: position.productId,
          ticker: product?.ticker || '',
          displayTicker: product?.displayTicker || '',
          side: position.side,
          size: position.totalIncreaseQuantity,
          entryPrice: divide(position.totalIncreaseNotional, position.totalIncreaseQuantity),
          exitPrice: divide(position.totalDecreaseNotional, position.totalDecreaseQuantity),
          pnl: position.realizedPnl,
          pnlPercentage: divide(position.realizedPnl, position.totalIncreaseNotional),
          feesAccruedUsd: position.feesAccruedUsd,
          openAt: position.createdAt,
          closeAt: position.updatedAt,
          baseTokenName: product?.baseTokenName || '',
          sizeDecimals: getDecimalPlaces(product?.lotSize || 0n),
          priceDecimals: getDecimalPlaces(product?.tickSize || 0n),
          fundingUsd: position.fundingUsd,
          isLiquidated: position.isLiquidated,
        } satisfies TableData;
      }),
    [positionWithProduct]
  );

  return (
    <>
      <PnlTableUi
        tableData={tableData || []}
        isPending={isPending}
        isFetching={isFetching || isFetchingNextPage}
        isError={isError}
        isConnected={isConnected}
        openPositionFillsModal={(positionId: string) =>
          setPositionFillsModal({ openOrClosed: 'open', positionId })
        }
        onShowPnlCardModal={(positionId: string) =>
          setPnlCardModal({ openOrClosed: 'open', positionId })
        }
        pageSize={pageSize}
        pagination={pagination}
        fetchNextPage={fetchNextPage}
        sorting={sorting}
        setSorting={setSorting}
        paginationKey={useClosedPositionParams}
        setPageSize={setPageSize}
        pageSizeOptions={pagination ? pageSizeOptions : [pageSize]}
        viewAllLink={
          portfolioPageEnabled && !pagination ? PORTFOLIO_TRADES_CLOSED_PNL_TAB_URL : undefined
        }
      />
      <PositionFillsModal />
    </>
  );
};
